import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabaseClient';
import {
  listAnnouncements,
  listDownloads,
  createAnnouncement,
  createDownload,
  updateAnnouncement,
  updateDownload,
  deleteAnnouncement,
  deleteDownload,
  getSiteConfig,
  setBrochure,
  getBrochureUrl,
} from '../services/contentService';

const DEFAULT_BUCKET = 'public-files';
const DEFAULT_DOWNLOAD_PATH = 'downloads/your-file.pdf';
const DEFAULT_ANNOUNCEMENT_PATH = 'announcements/your-file.pdf';
const RESERVED_BROCHURE_PATHS = new Set(['brochure.pdf', 'brochure/brochure.pdf']);

function slugifyFileName(name) {
  return String(name || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9._-]/g, '');
}

function withFolderPrefix(folder, fileNameOrPath) {
  const p = String(fileNameOrPath || '').replace(/^\/+/, '');
  if (!p) return '';
  if (p.startsWith(folder + '/')) return p;
  // if user pasted a full path like downloads/.. keep it
  if (p.includes('/')) return p;
  return `${folder}/${p}`;
}

function Admin() {
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState('');

  // Supabase Auth (email/password)
  const [session, setSession] = useState(null);
  const [email, setEmail] = useState('');
  const [pass, setPass] = useState('');

  const [tab, setTab] = useState('downloads'); // downloads | announcements | settings

  const [downloads, setDownloads] = useState([]);
  const [announcements, setAnnouncements] = useState([]);

  // form
  const [editingId, setEditingId] = useState(null); // when set => update
  const [bucket, setBucket] = useState(DEFAULT_BUCKET);
  const [path, setPath] = useState(DEFAULT_DOWNLOAD_PATH);
  const [file, setFile] = useState(null);

  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');

  // Settings: Brochure
  const [brochureBucket, setBrochureBucket] = useState(DEFAULT_BUCKET);
  const [brochurePath, setBrochurePath] = useState('brochure/brochure.pdf');
  const [brochureFile, setBrochureFile] = useState(null);
  const [brochureUrl, setBrochureUrl] = useState('');

  // New states for external link feature
  const [useExternalLink, setUseExternalLink] = useState(false);
  const [externalUrl, setExternalUrl] = useState('');

  const storageBaseUrl = useMemo(() => {
    const base = process.env.REACT_APP_SUPABASE_URL;
    if (!base) return '';
    return `${base.replace(/\/$/, '')}/storage/v1/object/public`;
  }, []);

  // publicUrl not needed anymore (we show a simple bucket/path preview instead)
  const brochurePublicUrl = storageBaseUrl ? `${storageBaseUrl}/${brochureBucket}/${brochurePath}` : '';

  const isDownloads = tab === 'downloads';
  const isSettings = tab === 'settings';

  const switchTab = (nextTab) => {
    setStatus('');
    setEditingId(null);
    setTitle('');
    setSubtitle('');
    setBucket(DEFAULT_BUCKET);
    setFile(null);

    if (nextTab === 'downloads') setPath(DEFAULT_DOWNLOAD_PATH);
    else if (nextTab === 'announcements') setPath(DEFAULT_ANNOUNCEMENT_PATH);

    setTab(nextTab);
  };

  // Keep placeholder path in sync if user switches tab without clicking our buttons (safety)
  useEffect(() => {
    if (editingId) return;
    if (tab === 'downloads') setPath(DEFAULT_DOWNLOAD_PATH);
    if (tab === 'announcements') setPath(DEFAULT_ANNOUNCEMENT_PATH);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // Auto-set path when a file is selected (works for add + edit)
  useEffect(() => {
    if (!file) return;
    const baseName = slugifyFileName(file.name) || 'file.pdf';
    const folder = isDownloads ? 'downloads' : 'announcements';
    const nextPath = withFolderPrefix(folder, baseName);

    // If user currently has brochure path (old data) or a placeholder, replace it immediately
    const current = String(path || '').trim();
    if (!current || RESERVED_BROCHURE_PATHS.has(current) || current === DEFAULT_DOWNLOAD_PATH || current === DEFAULT_ANNOUNCEMENT_PATH) {
      setPath(nextPath);
      return;
    }

    // If current path is just a filename (no folder), normalize
    if (!current.includes('/')) {
      setPath(withFolderPrefix(folder, current));
      return;
    }

    // Otherwise, keep what user typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, isDownloads]);

  useEffect(() => {
    // Load initial session and subscribe to changes
    supabase.auth.getSession().then(({ data }) => {
      setSession(data?.session || null);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
    });

    return () => {
      sub?.subscription?.unsubscribe?.();
    };
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setStatus('Signing in...');

    const { error } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password: pass,
    });

    if (error) {
      setStatus(error.message);
      return;
    }

    setStatus('');
  };

  const logout = async () => {
    await supabase.auth.signOut();
  };

  const refresh = async () => {
    const [d, a] = await Promise.all([listDownloads(), listAnnouncements()]);
    setDownloads(d);
    setAnnouncements(a);
  };

  const refreshSettings = async () => {
    const cfg = await getSiteConfig('brochure');
    const v = cfg?.value_json || null;
    if (v?.file_bucket) setBrochureBucket(v.file_bucket);
    if (v?.file_path) setBrochurePath(v.file_path);
    const url = await getBrochureUrl();
    setBrochureUrl(url);
  };

  useEffect(() => {
    if (!session) return;
    refresh().catch((e) => setStatus(e.message));
    refreshSettings().catch((e) => setStatus(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  const resetForm = () => {
    setEditingId(null);
    setTitle('');
    setSubtitle('');
    setBucket(DEFAULT_BUCKET);
    setPath(isDownloads ? DEFAULT_DOWNLOAD_PATH : DEFAULT_ANNOUNCEMENT_PATH);
    setFile(null);
    setUseExternalLink(false);
    setExternalUrl('');
  };

  const startEdit = (row) => {
    setStatus('');
    setEditingId(row.id ?? null);
    setTitle(row.title || '');
    setSubtitle(row.subtitle || '');
    setBucket(row.file_bucket || DEFAULT_BUCKET);
    setPath(row.file_path || '');
    setFile(null);

    const hasLink = !!row.file_url;
    setUseExternalLink(hasLink);
    setExternalUrl(row.file_url || '');

    // Scroll to form on smaller screens
    window?.scrollTo?.({ top: 0, behavior: 'smooth' });
  };

  const deleteItem = async (row) => {
    if (!row?.id) {
      setStatus('Delete failed: missing id');
      return;
    }

    const ok = window.confirm(`Delete this ${tab.slice(0, -1)}?\n\n${row.title}`);
    if (!ok) return;

    try {
      startProgress('Deleting...');
      bumpProgress(35);
      if (isDownloads) await deleteDownload(row.id);
      else await deleteAnnouncement(row.id);
      bumpProgress(70);

      if (editingId === row.id) resetForm();

      await refresh();
      finishProgress('Deleted');
      setStatus('Deleted');
    } catch (e) {
      setBusy(false);
      setProgress(0);
      setProgressLabel('');
      setStatus(`Delete failed: ${e.message}`);
    }
  };

  const uploadAndSave = async (e) => {
    e.preventDefault();
    setStatus('');

    if (!title.trim()) {
      setStatus('Title is required');
      return;
    }

    // Link mode
    if (useExternalLink) {
      if (!externalUrl.trim()) {
        setStatus('Link URL is required');
        return;
      }

      try {
        startProgress(editingId ? 'Updating...' : 'Saving...');
        bumpProgress(40);

        const payload = {
          title: title.trim(),
          subtitle: isDownloads ? null : (subtitle.trim() || null),
          file_url: externalUrl.trim(),
          file_bucket: null,
          file_path: null,
        };

        if (editingId) {
          if (isDownloads) await updateDownload(editingId, payload);
          else await updateAnnouncement(editingId, payload);
          setStatus('Updated');
        } else {
          if (isDownloads) await createDownload(payload);
          else await createAnnouncement(payload);
          setStatus('Saved');
        }

        bumpProgress(85);
        await refresh();
        bumpProgress(95);
        resetForm();
        finishProgress(editingId ? 'Updated' : 'Saved');
      } catch (err) {
        setBusy(false);
        setProgress(0);
        setProgressLabel('');
        setStatus(err?.message ? `Save failed: ${err.message}` : 'Save failed');
      }
      return;
    }

    // File mode
    if (!bucket.trim() || !path.trim()) {
      setStatus('File path is required');
      return;
    }

    if (!isSettings && bucket.trim() === DEFAULT_BUCKET && RESERVED_BROCHURE_PATHS.has(path.trim())) {
      setStatus('That file path is reserved for Brochure. Use downloads/... or announcements/...');
      return;
    }

    try {
      startProgress(editingId ? 'Updating...' : 'Saving...');
      bumpProgress(20);

      let finalPath = path.trim();
      if (file) {
        bumpProgress(30);
        const baseName = slugifyFileName(file.name);
        const folder = isDownloads ? 'downloads' : 'announcements';

        if (finalPath === DEFAULT_DOWNLOAD_PATH || finalPath === DEFAULT_ANNOUNCEMENT_PATH || !finalPath.includes('/')) {
          finalPath = withFolderPrefix(folder, baseName || 'file.pdf');
        }

        const { error } = await supabase.storage
          .from(bucket.trim())
          .upload(finalPath, file, { upsert: true, contentType: file.type || undefined });
        if (error) throw new Error(`Upload failed: ${error.message}`);
      } else {
        bumpProgress(35);
        const folder = isDownloads ? 'downloads' : 'announcements';
        if (finalPath === DEFAULT_DOWNLOAD_PATH || finalPath === DEFAULT_ANNOUNCEMENT_PATH || !finalPath.includes('/')) {
          finalPath = withFolderPrefix(folder, finalPath);
        }
      }

      bumpProgress(65);

      const payload = {
        title: title.trim(),
        subtitle: isDownloads ? null : (subtitle.trim() || null),
        file_bucket: bucket.trim(),
        file_path: finalPath,
        file_url: null,
      };

      if (editingId) {
        if (isDownloads) await updateDownload(editingId, payload);
        else await updateAnnouncement(editingId, payload);
        setStatus('Updated');
      } else {
        if (isDownloads) await createDownload(payload);
        else await createAnnouncement(payload);
        setStatus('Saved');
      }

      bumpProgress(85);
      await refresh();
      bumpProgress(95);
      resetForm();
      finishProgress(editingId ? 'Updated' : 'Saved');
    } catch (err) {
      setBusy(false);
      setProgress(0);
      setProgressLabel('');
      setStatus(err?.message ? `Save failed: ${err.message}` : 'Save failed');
    }
  };

  const saveBrochure = async (e) => {
    e.preventDefault();
    setStatus('');

    if (!brochureBucket.trim() || !brochurePath.trim()) {
      setStatus('Bucket and file path are required');
      return;
    }

    try {
      startProgress('Saving brochure...');
      bumpProgress(25);

      if (brochureFile) {
        const { error } = await supabase.storage
          .from(brochureBucket)
          .upload(brochurePath, brochureFile, { upsert: true, contentType: brochureFile.type || undefined });
        if (error) throw new Error(`Upload failed: ${error.message}`);
      }

      bumpProgress(70);
      await setBrochure({ file_url: null, file_bucket: brochureBucket.trim(), file_path: brochurePath.trim() });
      const url = await getBrochureUrl();
      setBrochureUrl(url);
      setBrochureFile(null);
      bumpProgress(95);
      finishProgress('Saved');
      setStatus('Saved');
    } catch (err) {
      setBusy(false);
      setProgress(0);
      setProgressLabel('');
      setStatus(err?.message ? `Save failed: ${err.message}` : 'Save failed');
    }
  };

  const items = isDownloads ? downloads : announcements;

  if (!session) {
    return (
      <div
        style={{
          maxWidth: 520,
          margin: '60px auto',
          padding: 24,
          fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, Arial',
        }}
      >
        <h1 style={{ margin: 0 }}>Admin Login</h1>
        <p style={{ opacity: 0.8, marginTop: 8 }}>Sign in with your Supabase email & password.</p>

        <form onSubmit={login} style={{ display: 'grid', gap: 12 }}>
          <label>
            Email
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{ width: '100%', padding: 10, marginTop: 6 }}
              placeholder="admin@yourdomain.com"
              required
            />
          </label>
          <label>
            Password
            <input
              type="password"
              value={pass}
              onChange={(e) => setPass(e.target.value)}
              style={{ width: '100%', padding: 10, marginTop: 6 }}
              required
            />
          </label>
          <button type="submit" style={{ padding: 10 }}>Sign in</button>
          {status ? <div style={{ color: 'crimson' }}>{status}</div> : null}
        </form>
      </div>
    );
  }

  const card = {
    background: '#fff',
    border: '1px solid rgba(0,0,0,0.08)',
    borderRadius: 14,
    boxShadow: '0 10px 30px rgba(0,0,0,0.06)',
  };

  const input = {
    width: '100%',
    padding: '10px 12px',
    marginTop: 6,
    borderRadius: 10,
    border: '1px solid rgba(0,0,0,0.18)',
    outline: 'none',
  };

  const btn = {
    padding: '10px 12px',
    borderRadius: 10,
    border: '1px solid rgba(0,0,0,0.18)',
    background: '#fff',
    cursor: 'pointer',
  };

  const btnPrimary = {
    ...btn,
    background: '#111827',
    color: '#fff',
    border: '1px solid #111827',
  };

  const tabBtn = (active) => ({
    ...btn,
    padding: '8px 12px',
    fontWeight: active ? 700 : 500,
    background: active ? '#111827' : '#fff',
    color: active ? '#fff' : '#111827',
    border: active ? '1px solid #111827' : '1px solid rgba(0,0,0,0.18)',
  });

  const metaChip = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 8px',
    borderRadius: 999,
    border: '1px solid rgba(0,0,0,0.12)',
    background: 'rgba(17,24,39,0.04)',
    fontSize: 12,
    color: '#111827',
  };

  const listCard = {
    ...card,
    padding: 14,
    borderRadius: 14,
  };

  const smallBtn = {
    padding: '7px 10px',
    borderRadius: 10,
    border: '1px solid rgba(0,0,0,0.18)',
    background: '#fff',
    cursor: 'pointer',
    fontWeight: 600,
  };

  const dangerBtn = {
    ...smallBtn,
    border: '1px solid rgba(220,38,38,0.55)',
    color: '#dc2626',
  };

  const linkBtn = {
    ...smallBtn,
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
  };

  const startProgress = (label) => {
    setBusy(true);
    setProgressLabel(label);
    setProgress(10);
  };

  const bumpProgress = (next) => {
    setProgress((p) => Math.max(p, Math.min(95, next)));
  };

  const finishProgress = (label = '') => {
    setProgress(100);
    setProgressLabel(label);
    // a short delay so user sees 100%
    setTimeout(() => {
      setBusy(false);
      setProgress(0);
      setProgressLabel('');
    }, 450);
  };

  return (
    <div
      style={{
        maxWidth: 1120,
        margin: '0 auto',
        padding: '28px 18px 60px',
        fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, Arial',
        color: '#111827',
        background: '#f6f7fb',
        minHeight: '100vh',
      }}
    >
      <div style={{ ...card, padding: 18, display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22 }}>Admin</h1>
          <div style={{ opacity: 0.85, marginTop: 4 }}>
            Signed in as <b>{session.user?.email}</b>
          </div>
          <div style={{ marginTop: 10, opacity: 0.8 }}>
            Manage <b>Downloads</b>, <b>Announcements</b>, and <b>Site Settings</b>.
          </div>
        </div>
        <button onClick={logout} style={btn}>Logout</button>
      </div>

      {(busy || progressLabel) ? (
        <div style={{ ...card, padding: 14, marginTop: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
            <div style={{ fontWeight: 700 }}>{progressLabel || 'Working...'}</div>
            <div style={{ fontVariantNumeric: 'tabular-nums', opacity: 0.8 }}>{Math.round(progress)}%</div>
          </div>
          <div style={{ height: 10, background: 'rgba(17,24,39,0.08)', borderRadius: 999, overflow: 'hidden', marginTop: 10 }}>
            <div style={{ height: '100%', width: `${progress}%`, background: '#111827', borderRadius: 999, transition: 'width 180ms ease' }} />
          </div>
          {status ? (
            <div style={{ marginTop: 10, color: status.toLowerCase().includes('fail') ? 'crimson' : 'inherit' }}>{status}</div>
          ) : null}
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: 10, margin: '16px 0', flexWrap: 'wrap' }}>
        <button onClick={() => switchTab('downloads')} style={tabBtn(tab === 'downloads')}>Downloads</button>
        <button onClick={() => switchTab('announcements')} style={tabBtn(tab === 'announcements')}>Announcements</button>
        <button onClick={() => switchTab('settings')} style={tabBtn(tab === 'settings')}>Site Settings</button>
      </div>

      {isSettings ? (
        <div style={{ maxWidth: 700 }}>
          <h2 style={{ marginTop: 0 }}>Site Settings</h2>

          <div style={{ border: '1px solid #eee', borderRadius: 12, padding: 14 }}>
            <h3 style={{ marginTop: 0 }}>Brochure</h3>
            <form onSubmit={saveBrochure} style={{ display: 'grid', gap: 12 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 12 }}>
                <label>
                  Bucket
                  <input value={brochureBucket} onChange={(e) => setBrochureBucket(e.target.value)} style={input} />
                </label>
                <label>
                  File path
                  <input value={brochurePath} onChange={(e) => setBrochurePath(e.target.value)} style={input} />
                </label>
              </div>

              <label>
                Upload file (optional)
                <input type="file" onChange={(e) => setBrochureFile(e.target.files?.[0] || null)} style={input} />
              </label>

              <div style={{ display: 'flex', gap: 10 }}>
                <button type="submit" style={btnPrimary}>Save</button>
                <button
                  type="button"
                  onClick={() => {
                    setBrochureFile(null);
                    refreshSettings().catch((e) => setStatus(e.message));
                  }}
                  style={btn}
                >
                  Reset
                </button>
              </div>

              <div>
                <div style={{ fontWeight: 600 }}>Public URL (based on bucket/path)</div>
                <a href={brochurePublicUrl} target="_blank" rel="noreferrer">{brochurePublicUrl || '(missing REACT_APP_SUPABASE_URL)'}</a>
              </div>

              <div>
                <div style={{ fontWeight: 600 }}>Current brochure link used by site</div>
                {brochureUrl ? (
                  <a href={brochureUrl} target="_blank" rel="noreferrer">{brochureUrl}</a>
                ) : (
                  <div style={{ opacity: 0.8 }}>Not set yet.</div>
                )}
              </div>

              {status ? (
                <div style={{ color: status.toLowerCase().includes('fail') ? 'crimson' : 'inherit' }}>{status}</div>
              ) : null}
            </form>
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1.15fr 0.85fr', gap: 24, alignItems: 'start' }}>
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
              <h2 style={{ marginTop: 0, marginBottom: 10 }}>Existing {tab}</h2>
              <div style={{ opacity: 0.75, fontSize: 13 }}>{items.length} item(s)</div>
            </div>

            <div style={{ display: 'grid', gap: 12 }}>
              {items.map((it) => {
                const selected = editingId && it.id === editingId;
                return (
                  <div
                    key={it.id || `${it.title}-${it.file_path}`}
                    style={{
                      ...listCard,
                      border: selected ? '1px solid rgba(17,24,39,0.45)' : listCard.border,
                      boxShadow: selected ? '0 12px 35px rgba(17,24,39,0.12)' : listCard.boxShadow,
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 800, fontSize: 16 }}>{it.title}</div>
                        {!isDownloads && it.subtitle ? (
                          <div style={{ opacity: 0.82, marginTop: 6, lineHeight: 1.4 }}>{it.subtitle}</div>
                        ) : null}

                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                          <span style={metaChip}>{it.file_bucket || 'bucket?'}</span>
                          <span style={metaChip} title={it.file_path || ''}>
                            {String(it.file_path || '').length > 38
                              ? `${String(it.file_path).slice(0, 20)}...${String(it.file_path).slice(-12)}`
                              : (it.file_path || '(no path)')}
                          </span>
                        </div>
                      </div>

                      <div style={{ display: 'flex', gap: 8, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        <button type="button" onClick={() => startEdit(it)} style={smallBtn}>Edit</button>
                        <button type="button" onClick={() => deleteItem(it)} style={dangerBtn}>Delete</button>
                        {it.url ? (
                          <a href={it.url} target="_blank" rel="noreferrer" style={linkBtn}>Open</a>
                        ) : null}
                      </div>
                    </div>
                  </div>
                );
              })}

              {items.length === 0 ? (
                <div style={{ ...card, padding: 16, opacity: 0.8 }}>
                  No items yet.
                </div>
              ) : null}
            </div>
          </div>

          <div>
            <h2 style={{ marginTop: 0 }}>{editingId ? 'Edit item' : 'Add item'}</h2>
            {editingId ? (
              <div style={{ marginBottom: 10, opacity: 0.8, fontSize: 13 }}>
                Editing ID: <code>{editingId}</code>
              </div>
            ) : null}

            {/* Right form */}
            <form onSubmit={uploadAndSave} style={{ display: 'grid', gap: 12, ...card, padding: 14 }}>
              <label>
                Title
                <input value={title} onChange={(e) => setTitle(e.target.value)} style={input} />
              </label>

              {!isDownloads ? (
                <label>
                  Subtext
                  <textarea value={subtitle} onChange={(e) => setSubtitle(e.target.value)} rows={3} style={{ ...input, resize: 'vertical' }} />
                </label>
              ) : null}

              <label style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <input type="checkbox" checked={useExternalLink} onChange={(e) => {
                  const v = e.target.checked;
                  setUseExternalLink(v);
                  setStatus('');
                  setFile(null);
                  if (v) {
                    // switching to link mode
                    setExternalUrl(externalUrl || '');
                  } else {
                    // switching to file mode
                    if (!path || RESERVED_BROCHURE_PATHS.has(String(path).trim())) {
                      setPath(isDownloads ? DEFAULT_DOWNLOAD_PATH : DEFAULT_ANNOUNCEMENT_PATH);
                    }
                  }
                }} />
                Use external link (Drive/Notice URL)
              </label>

              {useExternalLink ? (
                <label>
                  Link URL
                  <input value={externalUrl} onChange={(e) => setExternalUrl(e.target.value)} style={input} placeholder="https://..." />
                </label>
              ) : (
                <>
                  <label>
                    Upload file (optional)
                    <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} style={input} />
                  </label>

                  {/* Hide bucket/path inputs; keep auto-path logic and show a read-only preview */}
                  <div style={{ display: 'grid', gap: 6 }}>
                    <div style={{ fontSize: 13, opacity: 0.8 }}>File will be saved as</div>
                    <div style={{ fontWeight: 700, fontSize: 13 }}>
                      {bucket}/{path}
                    </div>
                    <div style={{ fontSize: 12, opacity: 0.7 }}>
                      (Stored in Supabase Storage. Folder is auto-selected based on tab.)
                    </div>
                  </div>
                </>
              )}

              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button type="submit" style={btnPrimary} disabled={busy}>{editingId ? 'Update' : 'Save'}</button>
                <button type="button" onClick={resetForm} style={btn} disabled={busy}>Clear</button>
              </div>

              {status ? (
                <div style={{ color: status.toLowerCase().includes('fail') ? 'crimson' : 'inherit' }}>{status}</div>
              ) : null}
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Admin;
